// src/pages/PracticeSession.js
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { fetchNextQuestion, submitAnswer, generateAIQuestions, submitTest } from "../api";
import RingChart from "../components/RingChart";

export default function PracticeSession() {
  const navigate = useNavigate();
  const query    = new URLSearchParams(window.location.search);
  const qTopic   = query.get("topic");
  const qMode    = query.get("mode");

  const config   = JSON.parse(sessionStorage.getItem("practiceConfig") || "{}");
  const topic    = qTopic || config.topic || "";
  const [mode, setMode] = useState(qMode || config.mode || "standard");

  const [questions,    setQuestions]   = useState([]);
  const [currIdx,      setCurrIdx]     = useState(-1);
  const [loading,      setLoading]     = useState(true);
  const [error,        setError]       = useState("");
  const [responses,    setResponses]   = useState({}); // { qId: { selected, feedback, time } }
  const [startTime,    setStartTime]   = useState(Date.now());
  const [marked,       setMarked]      = useState(new Set());
  const [showDrawer,   setShowDrawer]  = useState(false);
  
  const [streak,       setStreak]      = useState(0);
  const [done,         setDone]        = useState(0);
  const [correct,      setCorrect]     = useState(0);
  const [difficulty,   setDifficulty]  = useState("easy");
  const [skillScore,   setSkillScore]  = useState(0);
  const qStartRef  = useRef(Date.now());

  useEffect(() => { 
    const temp = sessionStorage.getItem("tempQuestions");
    if (temp) {
      try {
        const parsed = JSON.parse(temp);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setQuestions(parsed);
          setCurrIdx(0);
          setLoading(false);
          sessionStorage.removeItem("tempQuestions"); // Keep it for refreshing if needed, or clear it
          return;
        }
      } catch (e) {
        console.error("Failed to parse temp questions", e);
      }
    }
    loadNext(); 
  }, []); // eslint-disable-line

  async function loadNext() {
    // 1. Check if we reached the session limit (10 questions)
    if (questions.length >= 10 && currIdx === questions.length - 1) {
      if (responses[questions[currIdx]._id]) {
        setLoading(true);
        try {
          // Aggregate all responses for final submission
          const finalResponses = questions.map(q => ({
            questionId: q._id,
            selectedOptionIndex: responses[q._id]?.selected,
            timeTakenSeconds: responses[q._id]?.time || 30
          }));
          
          const result = await submitTest("practice", questions[0].topic, finalResponses, startTime);
          sessionStorage.setItem("testResult", JSON.stringify(result));
          navigate("/results");
        } catch (e) {
          setError("Failed to save results. Redirecting anyway...");
          setTimeout(() => navigate("/results"), 2000);
        } finally {
          setLoading(false);
        }
      }
      return;
    }

    // 2. If we are in history mode, just move pointer forward
    if (currIdx < questions.length - 1) {
      setCurrIdx(prev => prev + 1);
      qStartRef.current = Date.now();
      return;
    }

    // 3. Fetch NEW question
    setLoading(true);
    setError("");
    qStartRef.current = Date.now();

    try {
      const seenIds = questions.filter(q => !q.isTemporary).map(q => q._id);
      const resp = await fetchNextQuestion(topic, seenIds);
      const data = resp.question || resp;
      
      if (data && data._id) {
        if (resp.isFallback) {
          // Optional: signal to user that we switched subtopics
          console.log("Exhausted subtopic questions, showing related topics.");
        }
        
        if (resp.targetDifficulty) setDifficulty(resp.targetDifficulty);
        if (resp.skillScore !== undefined) setSkillScore(resp.skillScore);

        setQuestions(prev => [...prev, data]);
        setCurrIdx(prev => prev + 1);
      } else {
        throw new Error("No more questions found in the database. Try generating with AI.");
      }
    } catch (e) {
      console.error("Practice Error:", e);
      // Fallback to AI generation if DB fails
      try {
        const aiQs = await generateAIQuestions(topic, difficulty);
        if (aiQs && aiQs.length > 0) {
          const aiQ = aiQs[0];
          setQuestions(prev => [...prev, aiQ]);
          setCurrIdx(prev => prev + 1);
        } else {
          setError("Session complete! You've exhausted all available questions.");
        }
      } catch (aiErr) {
        setError("Network error or session complete. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  }

  function goTo(idx) {
    if (idx < 0 || idx >= questions.length) return;
    setCurrIdx(idx);
    qStartRef.current = Date.now();
  }

  function toggleMark() {
    const qId = questions[currIdx]._id;
    setMarked(prev => {
      const next = new Set(prev);
      if (next.has(qId)) next.delete(qId);
      else next.add(qId);
      return next;
    });
  }

  async function handleSelect(oi) {
    const q = questions[currIdx];
    const prevResp = responses[q?._id];
    if (prevResp || !q) return;

    const time = Math.round((Date.now() - qStartRef.current) / 1000);

    // If temporary, calculate locally
    if (q.isTemporary) {
      const isCorrect = oi === q.correctOptionIndex;
      const res = {
        isCorrect,
        correctOptionIndex: q.correctOptionIndex,
        explanation: q.explanation,
        skillScore: skillScore, // No change
        level: "demo",
        nextDifficulty: q.difficulty
      };
      setResponses(prev => ({
        ...prev,
        [q._id]: { selected: oi, feedback: res, time }
      }));
      setDone(d => d + 1);
      if (isCorrect) {
        setCorrect(c => c + 1);
        setStreak(s => s + 1);
      } else {
        setStreak(0);
      }
      return;
    }

    try {
      const res = await submitAnswer(q._id, oi, time);
      setResponses(prev => ({
        ...prev,
        [q._id]: { selected: oi, feedback: res, time }
      }));
      
      setDone(d => d + 1);
      if (res.isCorrect) {
        setCorrect(c => c + 1);
        setStreak(s => s + 1);
      } else {
        setStreak(0);
      }
      if (res.nextDifficulty) setDifficulty(res.nextDifficulty);
      if (res.skillScore !== undefined) setSkillScore(res.skillScore);
    } catch (e) {
      setError(e.message);
    }
  }

  const acc = done > 0 ? Math.round((correct / done) * 100) : 0;
  const diffCls = { easy: "tag-easy", medium: "tag-medium", hard: "tag-hard" };

      const q = questions[currIdx];
      const resp = responses[q?._id];
      const selected = resp?.selected;
      const feedback = resp?.feedback;
      const showResult = !!resp;
  const isMarked = q && marked.has(q._id);

  if (loading && questions.length === 0) {
    return (
      <div className="loading">
        <div className="spinner" />
        <span>Initializing practice session...</span>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 }}>
            <div>
              <h1 className="page-title">{topic || "Adaptive practice"}</h1>
              <p className="page-sub">Question {currIdx + 1} &middot; Difficulty adjusts based on performance</p>
            </div>

            {/* Session stats bar */}
            <div style={{ display: "flex", gap: 16, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", padding: "10px 16px", boxShadow: "var(--shadow-sm)" }}>
              {[["Done", done], ["Accuracy", `${acc}%`], ["Streak", `${streak} 🔥`]].map(([l, v]) => (
                <div key={l} style={{ textAlign: "center", minWidth: 50 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "var(--blue)" }}>{v}</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{l}</div>
                </div>
              ))}
              <div style={{ textAlign: "center", minWidth: 60 }}>
                <span className={`tag ${diffCls[difficulty] || "tag-easy"}`}>{difficulty}</span>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>difficulty</div>
              </div>
            </div>
          </div>

          {error && (
            <div className="error-box">
              {error} &nbsp;
              <button onClick={loadNext} style={{ textDecoration: "underline", background: "none", border: "none", cursor: "pointer", color: "inherit" }}>Retry</button>
            </div>
          )}

          {q && (
            <div className="two-col" style={{ gridTemplateColumns: "1fr minmax(auto, 280px)", alignItems: "start" }}>
              {/* Main question card */}
              <div className="card">
                {/* Meta */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <span className="tag" style={{ background: "var(--blue-bg)", color: "var(--blue-dark)" }}>{q.subject}</span>
                    <span className="tag" style={{ background: "var(--bg)", color: "var(--text-secondary)" }}>{q.topic}</span>
                    <span className={`tag ${diffCls[q.difficulty] || "tag-easy"}`}>{q.difficulty}</span>
                  </div>
                </div>

                {/* Question text */}
                <div style={{ fontSize: 15, lineHeight: 1.75, color: "var(--text-primary)", marginBottom: 20, fontWeight: 400 }}>
                  {q.questionText}
                </div>

                {/* Options */}
                {q.options.map((opt, i) => {
                  let cls = "option";
                  if (showResult) {
                    if (i === q.correctOptionIndex) cls += " correct";
                    else if (i === selected)         cls += " wrong";
                    else                             cls += " locked";
                  } else if (i === selected) cls += " selected";
                  return (
                    <div key={i} className={cls} onClick={() => handleSelect(i)}>
                      <div className="opt-label">{["A", "B", "C", "D"][i]}</div>
                      {opt}
                    </div>
                  );
                })}

                {/* Feedback box */}
                {showResult && feedback && (
                  <div className={`feedback-box ${feedback.isCorrect ? "feedback-correct" : "feedback-wrong"}`}>
                    <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6, color: feedback.isCorrect ? "var(--green-dark)" : "var(--red-dark)" }}>
                      {feedback.isCorrect ? "✅ Correct!" : "❌ Incorrect"}
                    </div>
                    <div style={{ fontSize: 13, color: feedback.isCorrect ? "var(--green-dark)" : "var(--red-dark)" }}>
                      Correct answer: <strong>{q.options[q.correctOptionIndex]}</strong>
                    </div>
                    {feedback.explanation && (
                      <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 6, fontStyle: "italic" }}>
                        💡 {feedback.explanation}
                      </div>
                    )}
                  </div>
                )}

                {/* Navigation Buttons */}
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24, gap: 8 }}>
                  <button
                    className="btn btn-sm"
                    disabled={currIdx === 0}
                    onClick={() => goTo(currIdx - 1)}
                  >
                    ← Previous
                  </button>
                  <button
                    className={`btn btn-sm ${isMarked ? "btn-primary" : ""}`}
                    style={{ background: isMarked ? "#602ba1" : "", color: isMarked ? "white" : "", flex: 1, justifyContent: "center" }}
                    onClick={toggleMark}
                  >
                    🚩 {isMarked ? "Added for Preview" : "Add for Preview"}
                  </button>
                  <button
                    className={`btn btn-sm ${!showResult && currIdx === questions.length - 1 ? "" : "btn-dark"}`}
                    disabled={(!showResult && currIdx === questions.length - 1) || loading}
                    onClick={loadNext}
                  >
                    {questions.length >= 10 && currIdx === questions.length - 1 ? "Finish Session" : currIdx === questions.length - 1 ? "Next Question →" : "History Next →"}
                  </button>
                </div>
              </div>

          {/* Sidebar */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {/* Skill ring */}
            <div className="card" style={{ textAlign: "center" }}>
              <div className="card-title">Your skill score</div>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
                <RingChart pct={Math.round(skillScore * 100)} size={80} strokeWidth={7} />
              </div>
              <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                {topic} performance
              </div>
            </div>

            {/* How it works */}
            <div className="card">
              <div className="card-title">How adaptive mode works</div>
              <div style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.7 }}>
                The engine targets your <strong style={{ color: "var(--red)" }}>weakest topic</strong> and serves questions at your current difficulty level.
                <br /><br />
                Answer <strong>3 correct in a row</strong> → difficulty increases.<br />
                Answer <strong>3 wrong in a row</strong> → difficulty decreases.
              </div>
            </div>

            <button className="btn btn-sm" onClick={() => navigate("/practice")}>
              ← Back to topics
            </button>
          </div>
        </div>
      )}

        {/* Floating Review List Button - Stacked above the global Notes icon */}
        <button
          className="notes-fab"
          style={{ background: "#602ba1", right: 24, bottom: (marked.size > 0 ? 94 : -100), transition: "bottom 0.4s" }}
          onClick={() => setShowDrawer(true)}
          title="View Marked Questions"
        >
          🚩
          {marked.size > 0 && (
            <span style={{ position: "absolute", top: -5, right: -5, background: "var(--red)", color: "white", fontSize: 10, padding: "2px 6px", borderRadius: 10 }}>
              {marked.size}
            </span>
          )}
        </button>

        {/* Review List Drawer */}
        <div className={`notepad-drawer ${showDrawer ? "open" : ""}`} style={{ width: 340 }}>
          <div className="notepad-header" style={{ background: "#602ba1", color: "white" }}>
            <div style={{ fontWeight: 600 }}>Marked for Preview</div>
            <button className="notepad-close" style={{ color: "white" }} onClick={() => setShowDrawer(false)}>×</button>
          </div>
          <div className="notepad-body" style={{ padding: 0 }}>
            {marked.size === 0 ? (
              <div style={{ padding: 30, textAlign: "center", color: "var(--text-muted)" }}>
                No questions marked for preview yet.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {questions.map((qu, i) => {
                  if (!marked.has(qu._id)) return null;
                  return (
                    <div
                      key={qu._id}
                      onClick={() => { goTo(i); setShowDrawer(false); }}
                      style={{
                        padding: "16px 20px",
                        borderBottom: "1px solid var(--border-soft)",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        background: i === currIdx ? "var(--blue-bg)" : "transparent"
                      }}
                    >
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#602ba1", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                        {i + 1}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          {qu.subject} · {qu.topic}
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)", lineHeight: 1.4 }}>
                          {qu.questionText.length > 80 ? qu.questionText.substring(0, 80) + "..." : qu.questionText}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="notepad-footer">
            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{marked.size} item(s) to review</div>
            <button className="btn btn-sm" onClick={() => setShowDrawer(false)}>Close</button>
          </div>
        </div>
      </div>
  );
}
